/**
 * Opportunities — funders this organization already bills, and funders it
 * qualifies for but does not bill yet.
 *
 * The scan matches CONFIRMED capabilities against the enabled sources. A
 * re-scan updates the score and reasons but never the status a person set, so
 * "Pursuing" and "Dismissed" survive. Drafting outreach hands off to the
 * Outreach tab, where nothing is sent until a person approves it.
 */
import React, { useMemo, useState } from 'react';
import { Mail, Radar, Target, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionHeader, PageState } from '@/components/weavein';
import { SOURCE_COPY, type FundingApi, type Opportunity, type ScanResult } from '@/services/api/fundingOpportunities';
import { CARD, MUTED, ErrorNote, Pill, STATUS_LABEL, formatDate, useLoad } from './shared';

const scoreTone = (s: number) => (s >= 70 ? 'success' : s >= 40 ? 'accent' : 'neutral') as 'success' | 'accent' | 'neutral';

const OpportunityCard: React.FC<{
  opp: Opportunity;
  onUpdate: (patch: Partial<Opportunity>) => Promise<void>;
  onDraft: () => Promise<void>;
}> = ({ opp, onUpdate, onDraft }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  const deadline = formatDate(opp.deadline);
  return (
    <li className={`${CARD} space-y-2 p-4`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-ink">{opp.funder_name}</p>
          <p className="text-[12px] text-ink-400">
            {opp.funder_kind.replace(/_/g, ' ')} · {SOURCE_COPY[opp.source_kind]?.name ?? opp.source_kind}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={scoreTone(opp.match_score)}>Match {opp.match_score}</Pill>
          <Pill tone={opp.status === 'dismissed' ? 'neutral' : opp.status === 'new' ? 'attention' : 'accent'}>
            {STATUS_LABEL[opp.status] ?? opp.status}
          </Pill>
          {deadline && <Pill tone="danger">Deadline {deadline}</Pill>}
        </div>
      </div>
      {opp.match_reasons.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-ink-500">
          {opp.match_reasons.map((r, i) => (
            <li key={i}>{r.reason}</li>
          ))}
        </ul>
      )}
      {opp.contact_email && (
        <p className="text-[12px] text-ink-400">
          Contact: {opp.contact_name ? `${opp.contact_name} · ` : ''}
          {opp.contact_email}
        </p>
      )}
      {!opp.is_existing && opp.status !== 'dismissed' && (
        <div className="flex flex-wrap gap-2 pt-1">
          {opp.status === 'new' || opp.status === 'reviewing' ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => onUpdate({ status: 'pursuing' }))}>
              <Target className="mr-1 h-3.5 w-3.5" /> Pursue
            </Button>
          ) : null}
          {opp.status !== 'outreach_sent' && opp.status !== 'won' && (
            <Button size="sm" disabled={busy} onClick={() => run(onDraft)}>
              <Mail className="mr-1 h-3.5 w-3.5" /> Draft outreach
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => onUpdate({ status: 'dismissed' }))}>
            <XCircle className="mr-1 h-3.5 w-3.5" /> Dismiss
          </Button>
        </div>
      )}
    </li>
  );
};

const OpportunitiesPanel: React.FC<{ api: FundingApi; onChanged: () => void; onOpenOutreach: () => void }> = ({
  api,
  onChanged,
  onOpenOutreach,
}) => {
  const { data, error, loading, reload, setData } = useLoad(() => api.listOpportunities(), [api]);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);

  const scan = async () => {
    setScanning(true);
    setActionError(null);
    try {
      setLastScan(await api.scan());
      await reload();
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setScanning(false);
    }
  };

  const update = async (id: number, patch: Partial<Opportunity>) => {
    setActionError(null);
    try {
      const next = await api.updateOpportunity(id, patch);
      setData((cur) => (cur ?? []).map((o) => (o.id === id ? next : o)));
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const draft = async (id: number) => {
    setActionError(null);
    try {
      await api.draftOutreach(id);
      setData((cur) => (cur ?? []).map((o) => (o.id === id ? { ...o, status: 'outreach_drafted' } : o)));
      onChanged();
      onOpenOutreach();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };

  const { existing, fresh, dismissed } = useMemo(() => {
    const all = [...(data ?? [])].sort((a, b) => b.match_score - a.match_score);
    return {
      existing: all.filter((o) => o.is_existing),
      fresh: all.filter((o) => !o.is_existing && o.status !== 'dismissed'),
      dismissed: all.filter((o) => !o.is_existing && o.status === 'dismissed'),
    };
  }, [data]);

  return (
    <div className="space-y-4">
      <div className={`${CARD} p-4`}>
        <SectionHeader
          title="Find funders"
          description="Matches your confirmed capabilities and service area against the sources you have on."
          action={
            <Button size="sm" onClick={scan} disabled={scanning}>
              <Radar className={`mr-1 h-3.5 w-3.5 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning…' : 'Scan now'}
            </Button>
          }
        />
        {lastScan && (
          <p className={`${MUTED} mt-2`}>
            Found {lastScan.found}: {lastScan.new} new, {lastScan.existing} you already bill.
          </p>
        )}
        {error && <div className="mt-2"><ErrorNote message={error} /></div>}
        {actionError && <div className="mt-2"><ErrorNote message={actionError} /></div>}
      </div>

      {loading && !data && <PageState variant="loading" />}
      {data && data.length === 0 && (
        <PageState
          variant="empty"
          title="No funders matched yet"
          message="Confirm your capabilities and service area, then scan. Matches show here with the reasons for each."
        />
      )}

      {existing.length > 0 && (
        <section className="space-y-2">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500">
            Funders you already bill ({existing.length})
          </p>
          <ul className="space-y-3">
            {existing.map((o) => (
              <OpportunityCard key={o.id} opp={o} onUpdate={(p) => update(o.id, p)} onDraft={() => draft(o.id)} />
            ))}
          </ul>
        </section>
      )}

      {fresh.length > 0 && (
        <section className="space-y-2">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500">
            New funders you qualify for ({fresh.length})
          </p>
          <ul className="space-y-3">
            {fresh.map((o) => (
              <OpportunityCard key={o.id} opp={o} onUpdate={(p) => update(o.id, p)} onDraft={() => draft(o.id)} />
            ))}
          </ul>
        </section>
      )}

      {dismissed.length > 0 && (
        <div>
          <button type="button" className={`${MUTED} underline`} onClick={() => setShowDismissed((v) => !v)}>
            {showDismissed ? 'Hide' : 'Show'} dismissed ({dismissed.length})
          </button>
          {showDismissed && (
            <ul className="mt-2 space-y-3">
              {dismissed.map((o) => (
                <li key={o.id} className={`${CARD} flex items-center justify-between p-3`}>
                  <span className="text-[13px] text-ink-500">{o.funder_name}</span>
                  <Button size="sm" variant="ghost" onClick={() => update(o.id, { status: 'reviewing' })}>
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPanel;
